/**
 * @file: useMemberList.ts
 * @copyright: (c) 2019-2021 sichuan zhichetech co., ltd.
 */
import {
  useCurrentStoreId,
  useCurrentUserId,
} from '@euler/app/flows/auth/context';
import { SimpleOrgMember } from '@euler/model';
import { OrgMember } from '@euler/model/entity';
import { OrgUserRoleType } from '@euler/model/enum';
import { useServiceFactory } from '@euler/services/factory';
import { useConditionalAsyncResource } from '@euler/utils/hooks';
import { useCallback } from 'react';

export const useMemberList = (
  role?: OrgUserRoleType,
  initialValues?: OrgMember[] | null,
) => {
  const { storeService } = useServiceFactory();
  const userId = useCurrentUserId();
  const fetch = useCallback(
    () =>
      storeService.listMembers({ role }).then(res => {
        const current = res.find(member => member.userId === userId);
        return current
          ? [current, ...res.filter(member => member.userId !== userId)]
          : res;
      }),
    [role, storeService, userId],
  );
  const storeId = useCurrentStoreId();
  return useConditionalAsyncResource(
    fetch,
    storeId ? `store/${storeId}/members/${role ?? '__all__'}` : null,
    initialValues,
    { autoClear: false },
  );
};

export const useSimpleMemberList = (): SimpleOrgMember[] | undefined => {
  const { storeService } = useServiceFactory();
  const fetch = useCallback(async () => {
    try {
      const result = await storeService.getSimpleMemberList();
      return result;
    } catch (e) {
      console.error(e);
    }
    return [];
  }, [storeService]);
  const storeId = useCurrentStoreId();
  return useConditionalAsyncResource(
    fetch,
    storeId ? `store/${storeId}/simple-member-list` : null,
    undefined,
    { autoClear: true },
  );
};
